import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Grid2,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBackIosNew as ArrowBackIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import {
  Customer,
  CustomerAlertType,
  CustomerStorageConfig,
  StorageChargeCurrency,
} from "../../../../../models/Customer/Customer";
import ActivityLogDrawer from "../../../../../views/components/common/drawer/ActivityLogDrawer";
import PartnerApiKeyDialog from "../../components/customer/PartnerApiKeyDialog";
import CustomerApiKeyList from "../../components/customer/CustomerApiKeyList";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

interface AddCustomerProps {
  setLoadingForPopup?: (loading: boolean) => void;
  isNavigationNeeded?: boolean;
  onClose?: (customer: Customer) => void;
  customer_id?: number;
}

const AddCustomer: React.FC<AddCustomerProps> = ({
  setLoadingForPopup,
  isNavigationNeeded = true,
  onClose,
  customer_id,
}) => {
  const { customerStore, userStore } = useStores();
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const c = theme.palette.customColors;
  const location = useLocation();
  const navigate = useNavigate();
  const { showError } = useNotification();

  const queryParams = new URLSearchParams(location.search);
  const customerId = customer_id || Number(queryParams.get("customer-id")) || 0;
  const listingSearch =
    (location.state as { listingSearch?: string } | null)?.listingSearch || "";
  const goToCustomerListing = () => {
    navigate({ pathname: "/customers/listing", search: listingSearch });
  };
  const storedCustomerId = Number(customerStore.customerDetails?.id) || 0;
  const customerDetailsMatch =
    Boolean(customerId) && storedCustomerId === Number(customerId);

  const [isLoading, setIsLoading] = useState(false);
  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [isActivityLoading, setIsActivityLoading] = useState(false);

  // Field validation and form message states (inline)
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formError, setFormError] = useState<string | null>(null);

  // UI-only Alert fields
  const [alert, setAlert] = useState(false);
  const [alertType, setAlertType] = useState<CustomerAlertType>(
    CustomerAlertType.EMAIL,
  );

  // Generate API Key Modal state
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [apiKeyRefreshTrigger, setApiKeyRefreshTrigger] = useState(0);

  const applyStoredAlert = () => {
    const config = customerStore.customerDetails?.config;
    setAlert(Boolean(config?.alert_enabled));
    if (
      config?.alert_type &&
      Object.values(CustomerAlertType).includes(
        config.alert_type as CustomerAlertType,
      )
    ) {
      setAlertType(config.alert_type as CustomerAlertType);
    } else {
      setAlertType(CustomerAlertType.EMAIL);
    }
  };

  useEffect(() => {
    if (!customerId) {
      customerStore.clearCustomerDetails();
      setAlert(false);
      setAlertType(CustomerAlertType.EMAIL);
      return;
    }

    if (Number(customerStore.customerDetails?.id) === Number(customerId)) {
      applyStoredAlert();
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setFormError(null);
    customerStore
      .getCustomerDetailsById(Number(customerId))
      .then(() => {
        if (cancelled) return;
        if (customerStore.customerDetails?.is_deleted) {
          showError(
            "Customer Inactive",
            "This customer is inactive. Activate the customer from the listing to view details.",
          );
          if (isNavigationNeeded) goToCustomerListing();
          return;
        }
        applyStoredAlert();
      })
      .catch((err) => {
        if (cancelled) return;
        setFormError(
          err?.response?.data?.message ||
            "Failed to load customer details. Please try again later.",
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const isNoAccess = (): boolean => {
    return (
      !userStore.hasPermission(EmployeeRoles.VIEW_CUSTOMER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_CUSTOMER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_CUSTOMER)
    );
  };

  const isViewOnly = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.VIEW_CUSTOMER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_CUSTOMER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_CUSTOMER)
    );
  };

  // API keys and storage charges are restricted to admins with the ALL permission.
  const hasAllPermission = userStore.hasPermission(EmployeeRoles.ALL);

  const canEditSalesOrderIds = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.ALL) ||
      userStore.hasPermission(EmployeeRoles.MANAGE_SALES_ANALYTICS)
    );
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    const customerDetails = customerStore.customerDetails;

    if (!customerDetails?.name || customerDetails.name.trim() === "") {
      newErrors.name = "Customer Name is required.";
    }

    if (customerDetails?.config?.send_redemption_email) {
      const redemptionEmail = customerDetails.config.email?.trim() ?? "";
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!redemptionEmail) {
        newErrors.email = "Redemption Email From is required.";
      } else if (!emailRegex.test(redemptionEmail)) {
        newErrors.email = "Please enter a valid email address.";
      }

      if (!customerDetails.config.from_email_name?.trim()) {
        newErrors.from_email_name =
          "Customer Redemption Name From is required.";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setFormError("Please fill all required fields correctly.");
      return false;
    }

    setFormError(null);
    return true;
  };

  const trimInputs = () => {
    customerStore.trimTextFields();
  };

  const saveDetails = () => {
    trimInputs();
    if (!validateForm()) return;
    customerStore.updateEnableAlert(alert);
    customerStore.updateAlertType(alert ? alertType : (null as any));
    if (customerId) {
      updateCustomer();
    } else {
      createCustomer();
    }
  };

  const createCustomer = () => {
    setIsLoading(true);
    setFormError(null);
    customerStore
      .createCustomer(
        {
          ...customerStore.customerDetails,
        },
        hasAllPermission,
      )
      .then((res) => {
        if (setLoadingForPopup) {
          if (onClose) onClose(res.result);
          return;
        }
        const newId = Number(res?.result?.id);
        if (newId) {
          // Land on edit/view of the new customer (same AddCustomer screen with id).
          navigate(`/customers/add-edit-customer?customer-id=${newId}`);
        } else {
          setIsLoading(false);
          setTimeout(() => {
            goToCustomerListing();
            customerStore.reset();
          }, 1500);
        }
      })
      .catch((err) => {
        setFormError(
          err?.response?.data?.message ||
            "Failed to create customer. Please try again later.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const updateCustomer = () => {
    setIsLoading(true);
    setFormError(null);
    customerStore
      .updateCustomerDetails(hasAllPermission)
      .then((res) => {
        if (setLoadingForPopup && onClose) {
          onClose(res);
        }
      })
      .catch((err) => {
        setFormError(
          err?.response?.data?.message ||
            "Failed to update customer. Please try again later.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const resetForm = () => {
    customerStore.discardChanges();
    if (!customerId) {
      customerStore.clearCustomerDetails();
      setAlert(false);
      setAlertType(CustomerAlertType.EMAIL);
    } else {
      const config = customerStore.customerDetails?.config;
      setAlert(Boolean(config?.alert_enabled));
      if (
        config?.alert_type &&
        Object.values(CustomerAlertType).includes(
          config.alert_type as CustomerAlertType,
        )
      ) {
        setAlertType(config.alert_type as CustomerAlertType);
      } else {
        setAlertType(CustomerAlertType.EMAIL);
      }
    }
    setErrors({});
    setFormError(null);
  };

  const handleOpenActivityDrawer = () => {
    if (!customerId) return;
    setIsActivityDrawerOpen(true);
    setIsActivityLoading(true);
    customerStore
      .getCustomerActivityLogs(Number(customerId))
      .catch((err) => console.log("error in getCustomerActivityLogs", err))
      .finally(() => setIsActivityLoading(false));
  };

  const selectStyles = {
    borderRadius: "8px",
    height: 42,
    backgroundColor: colors.surface,
    fontSize: "0.875rem",
    color: colors.title,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accentPurple,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accentPurple,
      borderWidth: 1.5,
    },
    "&.Mui-disabled": {
      backgroundColor: c.lightGrayBackground,
    },
  };

  const cardStyles = {
    backgroundColor: colors.surface,
    borderRadius: 3,
    border: `1px solid ${colors.border}`,
    boxShadow: `0 1px 3px ${c.shadowVeryLight}`,
    p: { xs: 2.5, sm: 3.5 },
    mb: 3,
  };

  /** Top-level section headings sit above cards (outside the box). */
  const sectionTitleProps = {
    variant: "heading6",
    fontWeight: 700,
    color: c.darkBlueText,
    sx: { mb: 2 },
  } as const;

  const labelProps = {
    variant: "heading8",
    color: c.navyText,
    sx: { mb: 0.75 },
  } as const;

  /** Nested feature cards in a Grid2 row. */
  const featureCardStyles = {
    ...cardStyles,
    mb: 0,
    height: "100%",
  };

  const emailRedemptionEnabled = Boolean(
    customerStore.customerDetails?.config?.send_redemption_email,
  );
  if (customerId && !customerDetailsMatch) {
    return (
      <Box
        sx={{
          backgroundColor: colors.pageBg,
          minHeight: "40vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          py: 6,
        }}
      >
        {formError ? (
          <>
            <CustomTypography variant="body2" color={c.errorRed}>
              {formError}
            </CustomTypography>
            {isNavigationNeeded ? (
              <AppButton
                label="Back to customers"
                variant="transparentPrimary"
                onClick={goToCustomerListing}
              />
            ) : null}
          </>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: colors.pageBg, minHeight: "100vh", py: 3 }}>
      {/* Same save feedback pattern as Add Order: full-screen loader blink, no success text */}
      <Backdrop
        sx={{
          color: c.white,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Container maxWidth="xl">
        {/* Header Bar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          {/* Title & Subtitle with Back Button */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={() =>
                isNavigationNeeded
                  ? goToCustomerListing()
                  : onClose && onClose(customerStore.customerDetails)
              }
              sx={{
                mt: 0.5,
                p: 0.75,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.surface,
                color: c.mutedSlateText,
                "&:hover": {
                  backgroundColor: c.lightGrayBackground,
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Box>
              <CustomTypography
                variant="heading3"
                color={colors.title}
                style={{ fontSize: undefined }}
                sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
              >
                {customerId ? "Update Customer" : "Create Customer"}
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={colors.subtitle}
                style={{ marginTop: 2 }}
              >
                {customerId
                  ? "Modify customer records, credentials, and custom integration settings."
                  : "Add customer records, credentials, and custom integration settings."}
              </CustomTypography>
            </Box>
          </Box>

          {/* Action Buttons in Header: ONLY show in Update mode, NOT in Create mode */}
          {Boolean(customerId) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {hasAllPermission && (
                <AppButton
                  label="Generate API Key"
                  variant="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setIsApiKeyModalOpen(true)}
                />
              )}
              <AppButton
                label="Activity log"
                variant="grayscale"
                onClick={handleOpenActivityDrawer}
              />
            </Box>
          )}
        </Box>

        {/* Permission Denied View */}
        {isNoAccess() ? (
          <Paper sx={{ p: 4, borderRadius: "12px", textAlign: "center" }}>
            <CustomTypography
              variant="heading6"
              fontWeight={500}
              color={colors.subtitle}
            >
              You do not have permission to view this section.
            </CustomTypography>
          </Paper>
        ) : (
          <Box>
            {/* Basic Information — title outside box */}
            <CustomTypography {...sectionTitleProps}>
              Basic Information
            </CustomTypography>
            <Paper elevation={0} sx={cardStyles}>
              <Grid2 container spacing={2.5}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <CustomTypography {...labelProps}>
                    Is it partner ?
                  </CustomTypography>
                  <Select
                    fullWidth
                    size="small"
                    value={
                      customerStore.customerDetails?.is_partner
                        ? "true"
                        : "false"
                    }
                    onChange={(e) =>
                      customerStore.updateIsPartner(e.target.value === "true")
                    }
                    disabled={isViewOnly()}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={selectStyles}
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <CustomTypography {...labelProps}>
                    Customer Name{" "}
                    <Box component="span" sx={{ color: colors.error }}>
                      *
                    </Box>
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    placeholder="Customer Name"
                    value={customerStore.customerDetails?.name || ""}
                    onChange={(e) => {
                      customerStore.updateName(e.target.value);
                      if (errors.name) {
                        setErrors((prev) => ({ ...prev, name: "" }));
                        setFormError(null);
                      }
                    }}
                    onBlur={trimInputs}
                    preventEdgeSpaces
                    helperText={errors.name}
                    disabled={isViewOnly()}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <CustomTypography {...labelProps}>
                    Legal Name
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    placeholder="Legal Name"
                    value={customerStore.customerDetails?.legal_name || ""}
                    onChange={(e) =>
                      customerStore.updateLegalName(e.target.value)
                    }
                    onBlur={trimInputs}
                    preventEdgeSpaces
                    disabled={isViewOnly()}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <CustomTypography {...labelProps}>Zoho Id</CustomTypography>
                  <CustomTextField
                    width="100%"
                    placeholder="Zoho Id"
                    value={customerStore.customerDetails?.zoho_id || ""}
                    onChange={(e) => customerStore.updateZohoId(e.target.value)}
                    onBlur={trimInputs}
                    preventEdgeSpaces
                    disabled={
                      isViewOnly() ||
                      !(
                        userStore.hasPermission(EmployeeRoles.ALL) ||
                        userStore.hasPermission(EmployeeRoles.EDIT_CUSTOMER)
                      )
                    }
                  />
                </Grid2>
              </Grid2>
            </Paper>

            {/* Sales — title outside box */}
            <CustomTypography {...sectionTitleProps}>Sales</CustomTypography>
            <Paper elevation={0} sx={cardStyles}>
              <Grid2 container spacing={2.5}>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <CustomTypography {...labelProps}>
                    Marketing Order Id
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    placeholder="Marketing Order Id"
                    value={
                      customerStore.customerDetails?.marketing_order_id || ""
                    }
                    onChange={(e) =>
                      customerStore.updateMarketingOrderId(e.target.value)
                    }
                    onBlur={trimInputs}
                    preventEdgeSpaces
                    disabled={isViewOnly() || !canEditSalesOrderIds()}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <CustomTypography {...labelProps}>
                    HR Order Id
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    placeholder="HR Order Id"
                    value={customerStore.customerDetails?.hr_order_id || ""}
                    onChange={(e) =>
                      customerStore.updateHrOrderId(e.target.value)
                    }
                    onBlur={trimInputs}
                    preventEdgeSpaces
                    disabled={isViewOnly() || !canEditSalesOrderIds()}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 4 }}>
                  <CustomTypography {...labelProps}>
                    Goldstandard Order Id
                  </CustomTypography>
                  <CustomTextField
                    width="100%"
                    placeholder="Goldstandard Order Id"
                    value={
                      customerStore.customerDetails?.goldstandard_order_id || ""
                    }
                    onChange={(e) =>
                      customerStore.updateGoldstandardOrderId(e.target.value)
                    }
                    onBlur={trimInputs}
                    preventEdgeSpaces
                    disabled={isViewOnly() || !canEditSalesOrderIds()}
                  />
                </Grid2>
              </Grid2>
            </Paper>

            {/* Features — top-level title outside; each capability in its own box */}
            <CustomTypography {...sectionTitleProps}>Features</CustomTypography>

            <Grid2 container spacing={3} sx={{ mb: 3 }}>
              <Grid2
                size={{ xs: 12, sm: emailRedemptionEnabled ? 12 : 6 }}
              >
                <Paper elevation={0} sx={featureCardStyles}>
                  <CustomTypography {...sectionTitleProps}>
                    Redemption
                  </CustomTypography>
                  <Grid2 container spacing={2.5}>
                    <Grid2
                      size={{ xs: 12, sm: emailRedemptionEnabled ? 6 : 12 }}
                    >
                      <CustomTypography {...labelProps}>
                        Email Redemption
                      </CustomTypography>
                      <Select
                        fullWidth
                        size="small"
                        value={emailRedemptionEnabled ? "true" : "false"}
                        onChange={(e) => {
                          const enabled = e.target.value === "true";
                          customerStore.updateEnableRedemptionEmail(enabled);
                          if (!enabled) {
                            setErrors((prev) => ({
                              ...prev,
                              email: "",
                              from_email_name: "",
                            }));
                            setFormError(null);
                          }
                        }}
                        disabled={isViewOnly()}
                        IconComponent={KeyboardArrowDownIcon}
                        sx={selectStyles}
                      >
                        <MenuItem value="true">True</MenuItem>
                        <MenuItem value="false">False</MenuItem>
                      </Select>
                    </Grid2>

                    {emailRedemptionEnabled && (
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <CustomTypography {...labelProps}>
                          Redirect to LinkedIn
                        </CustomTypography>
                        <Select
                          fullWidth
                          size="small"
                          value={
                            customerStore.customerDetails?.config
                              ?.redirect_redemptions_to_linkedin !==
                              undefined &&
                            customerStore.customerDetails?.config
                              ?.redirect_redemptions_to_linkedin !== null
                              ? Boolean(
                                  customerStore.customerDetails.config
                                    .redirect_redemptions_to_linkedin,
                                )
                                ? "true"
                                : "false"
                              : "false"
                          }
                          onChange={(e) =>
                            customerStore.updateRedirectToLinkedIn(
                              e.target.value === "true",
                            )
                          }
                          disabled={isViewOnly()}
                          IconComponent={KeyboardArrowDownIcon}
                          sx={selectStyles}
                        >
                          <MenuItem value="true">Yes</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      </Grid2>
                    )}

                    {emailRedemptionEnabled && (
                      <>
                        <Grid2 size={{ xs: 12, sm: 6 }}>
                          <CustomTypography {...labelProps}>
                            Redemption Email From *
                          </CustomTypography>
                          <CustomTextField
                            width="100%"
                            placeholder="rewards@redeem.swageazy.com"
                            value={
                              customerStore.customerDetails?.config?.email ??
                              ""
                            }
                            onChange={(e) => {
                              customerStore.updateRedemptionEmail(
                                e.target.value,
                              );
                              if (errors.email) {
                                setErrors((prev) => ({ ...prev, email: "" }));
                                setFormError(null);
                              }
                            }}
                            onBlur={trimInputs}
                    preventEdgeSpaces
                            helperText={errors.email}
                            disabled={isViewOnly()}
                          />
                        </Grid2>

                        <Grid2 size={{ xs: 12, sm: 6 }}>
                          <CustomTypography {...labelProps}>
                            Customer Redemption Name From *
                          </CustomTypography>
                          <CustomTextField
                            width="100%"
                            placeholder="Add Name"
                            value={
                              customerStore.customerDetails?.config
                                ?.from_email_name || ""
                            }
                            onChange={(e) => {
                              customerStore.updateEmailFrom(e.target.value);
                              if (errors.from_email_name) {
                                setErrors((prev) => ({
                                  ...prev,
                                  from_email_name: "",
                                }));
                                setFormError(null);
                              }
                            }}
                            onBlur={trimInputs}
                    preventEdgeSpaces
                            helperText={errors.from_email_name}
                            disabled={isViewOnly()}
                          />
                        </Grid2>
                      </>
                    )}
                  </Grid2>
                </Paper>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Paper elevation={0} sx={featureCardStyles}>
                  <CustomTypography {...sectionTitleProps}>
                    Inventory
                  </CustomTypography>
                  <CustomTypography {...labelProps}>Inventory</CustomTypography>
                  <Select
                    fullWidth
                    size="small"
                    value={
                      Boolean(
                        customerStore.customerDetails?.config
                          ?.inventory_enabled,
                      )
                        ? "true"
                        : "false"
                    }
                    onChange={(e) =>
                      customerStore.updateEnableInventory(
                        e.target.value === "true",
                      )
                    }
                    disabled={isViewOnly()}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={selectStyles}
                  >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </Select>
                </Paper>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Paper elevation={0} sx={featureCardStyles}>
                  <CustomTypography {...sectionTitleProps}>
                    GOC
                  </CustomTypography>
                  <CustomTypography {...labelProps}>GOC</CustomTypography>
                  <Select
                    fullWidth
                    size="small"
                    value={
                      Boolean(
                        customerStore.customerDetails?.config
                          ?.gift_of_choice_enabled,
                      )
                        ? "true"
                        : "false"
                    }
                    onChange={(e) =>
                      customerStore.updateEnableGOC(e.target.value === "true")
                    }
                    disabled={isViewOnly()}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={selectStyles}
                  >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </Select>
                </Paper>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Paper elevation={0} sx={featureCardStyles}>
                  <CustomTypography {...sectionTitleProps}>
                    Campaign
                  </CustomTypography>
                  <CustomTypography {...labelProps}>Campaign</CustomTypography>
                  <Select
                    fullWidth
                    size="small"
                    value={
                      Boolean(
                        customerStore.customerDetails?.config
                          ?.campaigns_enabled,
                      )
                        ? "true"
                        : "false"
                    }
                    onChange={(e) =>
                      customerStore.updateEnableCampaign(
                        e.target.value === "true",
                      )
                    }
                    disabled={isViewOnly()}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={selectStyles}
                  >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </Select>
                </Paper>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Paper elevation={0} sx={featureCardStyles}>
                  <CustomTypography {...sectionTitleProps}>
                    Swagstore
                  </CustomTypography>
                  <CustomTypography {...labelProps}>Swagstore</CustomTypography>
                  <Select
                    fullWidth
                    size="small"
                    value={
                      Boolean(
                        customerStore.customerDetails?.config
                          ?.swagstore_enabled,
                      )
                        ? "true"
                        : "false"
                    }
                    onChange={(e) =>
                      customerStore.updateEnableSwagStore(
                        e.target.value === "true",
                      )
                    }
                    disabled={isViewOnly()}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={selectStyles}
                  >
                    <MenuItem value="true">True</MenuItem>
                    <MenuItem value="false">False</MenuItem>
                  </Select>
                </Paper>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: alert ? 12 : 6 }}>
                <Paper elevation={0} sx={featureCardStyles}>
                  <CustomTypography {...sectionTitleProps}>
                    Alerts
                  </CustomTypography>
                  <Grid2 container spacing={2.5}>
                    <Grid2 size={{ xs: 12, sm: alert ? 6 : 12 }}>
                      <CustomTypography {...labelProps}>Alert</CustomTypography>
                      <Select
                        fullWidth
                        size="small"
                        value={alert ? "true" : "false"}
                        onChange={(e) => {
                          const isAlert = e.target.value === "true";
                          setAlert(isAlert);
                          customerStore.updateEnableAlert(isAlert);
                        }}
                        disabled={isViewOnly()}
                        IconComponent={KeyboardArrowDownIcon}
                        sx={selectStyles}
                      >
                        <MenuItem value="true">True</MenuItem>
                        <MenuItem value="false">False</MenuItem>
                      </Select>
                    </Grid2>

                    {alert && (
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <CustomTypography {...labelProps}>
                          Alert Type
                        </CustomTypography>
                        <Select
                          fullWidth
                          size="small"
                          value={alertType}
                          onChange={(e) => {
                            const type = e.target.value as CustomerAlertType;
                            setAlertType(type);
                            customerStore.updateAlertType(type);
                          }}
                          disabled={isViewOnly()}
                          IconComponent={KeyboardArrowDownIcon}
                          sx={selectStyles}
                        >
                          <MenuItem value={CustomerAlertType.EMAIL}>
                            EMAIL
                          </MenuItem>
                          <MenuItem value={CustomerAlertType.SMS}>SMS</MenuItem>
                          <MenuItem value={CustomerAlertType.WHATSAPP}>
                            WHATSAPP
                          </MenuItem>
                        </Select>
                      </Grid2>
                    )}
                  </Grid2>
                </Paper>
              </Grid2>
            </Grid2>

            {/* Storage Charge — title outside box */}
            {hasAllPermission && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box>
                    <CustomTypography {...sectionTitleProps} sx={{ mb: 0.5 }}>
                      Storage Charge
                    </CustomTypography>
                    <CustomTypography variant="body2" color={colors.subtitle}>
                      Set labour, package, and storage charges for each size.
                    </CustomTypography>
                  </Box>
                  <Box sx={{ minWidth: 120 }}>
                    <Select
                      size="small"
                      value={
                        customerStore.customerDetails?.storage_config
                          ?.currency || StorageChargeCurrency.USD
                      }
                      onChange={(e) =>
                        customerStore.updateStorageCurrency(
                          e.target.value as StorageChargeCurrency,
                        )
                      }
                      disabled={isViewOnly()}
                      IconComponent={KeyboardArrowDownIcon}
                      sx={{
                        ...selectStyles,
                        width: 120,
                      }}
                    >
                      <MenuItem value={StorageChargeCurrency.INR}>INR</MenuItem>
                      <MenuItem value={StorageChargeCurrency.USD}>USD</MenuItem>
                    </Select>
                  </Box>
                </Box>
                <Paper elevation={0} sx={cardStyles}>
                  <TableContainer
                    sx={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: 2,
                      overflowX: "auto",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: colors.headerBg }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              color: colors.label,
                              py: 1.5,
                              px: 2,
                              width: "12%",
                            }}
                          >
                            Size
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              color: colors.label,
                              py: 1.5,
                              px: 2,
                              width: "29.33%",
                            }}
                          >
                            Labour Charge/Hrs
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              color: colors.label,
                              py: 1.5,
                              px: 2,
                              width: "29.33%",
                            }}
                          >
                            Package Charge/Hrs
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.8rem",
                              color: colors.label,
                              py: 1.5,
                              px: 2,
                              width: "29.33%",
                            }}
                          >
                            Storage Charge/Hrs
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(
                          [
                            { key: "s", label: "S" },
                            { key: "m", label: "M" },
                            { key: "l", label: "L" },
                            { key: "sp", label: "SP" },
                          ] as const
                        ).map((sizeRow) => (
                          <TableRow
                            key={sizeRow.key}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                              "&:hover": {
                                backgroundColor: colors.filterHover,
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                color: colors.title,
                                px: 2,
                                py: 1.5,
                              }}
                            >
                              {sizeRow.label}
                            </TableCell>
                            {(
                              [
                                "labour_charge",
                                "package_charge",
                                "storage_charge",
                              ] as const
                            ).map((field) => {
                              const propKey =
                                `${sizeRow.key}_${field}` as keyof CustomerStorageConfig;
                              const rawVal =
                                customerStore.customerDetails?.storage_config?.[
                                  propKey
                                ];
                              const val =
                                rawVal !== undefined && rawVal !== null
                                  ? String(rawVal)
                                  : "";
                              return (
                                <TableCell key={field} sx={{ px: 2, py: 1 }}>
                                  <CustomTextField
                                    width="100%"
                                    type="number"
                                    allowNegativeValue={false}
                                    placeholder="Text field"
                                    value={val}
                                    onChange={(e) => {
                                      const sanitized = e.target.value.replace(
                                        /[^0-9.]/g,
                                        "",
                                      );
                                      const parts = sanitized.split(".");
                                      const formatted =
                                        parts.length > 2
                                          ? `${parts[0]}.${parts.slice(1).join("")}`
                                          : sanitized;
                                      customerStore.updateStorageCharge(
                                        sizeRow.key,
                                        field,
                                        formatted,
                                      );
                                    }}
                                    onBlur={trimInputs}
                                    disabled={isViewOnly()}
                                  />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )}

            {/* Inline Error Message Banner */}
            {formError && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: `${colors.error}14`,
                    border: `1px solid ${colors.error}40`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    maxWidth: 600,
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <ErrorOutlineIcon
                      sx={{ fontSize: 18, color: colors.error }}
                    />
                    <CustomTypography
                      variant="body2"
                      color={colors.error}
                      fontWeight={500}
                    >
                      {formError}
                    </CustomTypography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setFormError(null)}
                    sx={{ color: colors.error, p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Bottom Action Buttons: Centered */}
            {!isViewOnly() && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  mt: formError ? 2 : 4,
                  mb: 6,
                }}
              >
                <AppButton
                  label="Reset"
                  variant="grayscale"
                  onClick={resetForm}
                  customStyles={{ minWidth: 140, height: 44 }}
                />
                <AppButton
                  label={customerId ? "Save" : "Create"}
                  variant="primary"
                  onClick={saveDetails}
                  disabled={isLoading}
                  customStyles={{ minWidth: 140, height: 44 }}
                />
              </Box>
            )}

            {/* Customer Partner API Keys List */}
            {Boolean(customerId) && hasAllPermission && (
              <CustomerApiKeyList
                customerId={Number(customerId)}
                refreshTrigger={apiKeyRefreshTrigger}
              />
            )}
          </Box>
        )}

        {/* Generate API Key Dialog */}
        {hasAllPermission && (
          <PartnerApiKeyDialog
            isOpen={isApiKeyModalOpen}
            onClose={() => {
              setIsApiKeyModalOpen(false);
              setApiKeyRefreshTrigger((prev) => prev + 1);
            }}
            customerId={Number(customerId)}
            customerName={customerStore.customerDetails?.name}
          />
        )}

        {/* Activity Log Drawer */}
        <ActivityLogDrawer
          isOpen={isActivityDrawerOpen}
          onClose={() => setIsActivityDrawerOpen(false)}
          activityLogs={customerStore.activityLogs}
          isLoading={isActivityLoading}
          title="Activity History"
          subtitle={customerId ? `Customer #${customerId}` : null}
        />
      </Container>
    </Box>
  );
};

export default observer(AddCustomer);
